/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { useAuth0 } from "@auth0/auth0-react";
import { AuthApi, BaseAPI, Configuration } from "client";
import { useUserContext } from "context/UserContext";
import { HttpError } from "exception/HttpError";
import { TimeoutError } from "exception/TimeoutError";
import { useCallback, useRef, useMemo } from "react";
import { globalVariable } from "utils/Api";

/**
 * API Call with timeout.
 * @param {T} Api then API instance
 * @param {number} timeout timeout[ms]
 * @param {boolean} useAuth using auth0
 * @return {[T, function]} the API instance with timeout. cancel function.
 */
export function useApiWithTimeout<T extends BaseAPI>(
  Api: { new (config: Configuration): T },
  timeout: number,
  useAuth: boolean = true
): [T, () => void] {
  const { roleName } = useUserContext();
  const { getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const controller = useRef<AbortController>();
  const timerId = useRef<any | undefined>();

  const cancel = useCallback(() => {
    controller.current?.abort();
    clearTimeout(timerId.current);
  }, []);

  const getToken = useCallback(async () => {
    let currentToken;
    try {
      currentToken = await getAccessTokenSilently();
    } catch (err) {
      // If token error, then re-login.
      logout();
      return globalVariable.accessToken;
    }
    if (roleName !== undefined && globalVariable.accessToken !== currentToken) {
      console.info("token refreshed");
      const authApi = new AuthApi(
        new Configuration({
          basePath: process.env.REACT_APP_UI_SERVER_ENDPOINT,
          apiKey: currentToken,
        })
      );
      const role = await authApi.updateToken();
      if (roleName !== role.roleName) {
        // If role changed, then re-login.
        loginWithRedirect();
      }
    }
    globalVariable.accessToken = currentToken;
    return currentToken;
  }, [roleName, getAccessTokenSilently, logout, loginWithRedirect]);

  const newApi = useMemo(() => {
    return new Api(
      new Configuration({
        basePath: process.env.REACT_APP_UI_SERVER_ENDPOINT,
        apiKey: useAuth ? getToken : undefined,
      })
    ).withMiddleware({
      pre: async (context) => {
        // cancel before called api.
        controller.current?.abort("Cancel");
        clearTimeout(timerId.current);

        const abortController = new AbortController();
        timerId.current = setTimeout(() => {
          abortController.abort("Timeout");
        }, timeout);
        context.init.signal = abortController.signal;
        controller.current = abortController;
      },
      onError: async (context) => {
        if (
          context.error instanceof DOMException &&
          context.error.name === "AbortError"
        ) {
          // timer is already clear.
          console.debug("request aborted:" + context.url);
          if (context.init.signal?.reason === "Timeout") {
            // throw timeout error.
            throw new TimeoutError(context.url);
          }
          // throw abort error.
          throw context.error;
        } else {
          clearTimeout(timerId.current);
          if (context.response) {
            const errorResponse = await context.response.json();
            // throw error with status and errorcode
            throw new HttpError(
              context.response,
              context.response.status,
              errorResponse.errorCodes,
              context.error,
              context.url
            );
          }
          // throw cause error.
          throw context.error;
        }
      },
      post: async (context) => {
        clearTimeout(timerId.current);
        if (context.response.status >= 400) {
          const errorResponse = await context.response.json();
          // throw error with status and errorcode
          throw new HttpError(
            context.response,
            context.response.status,
            errorResponse.errors,
            undefined,
            context.url
          );
        }
      },
    });
  }, [Api, getToken, timeout, useAuth]);
  return [newApi, cancel];
}
